import {Dayjs} from 'dayjs';

import {PostStepEnum} from '../../page/main/posts/post-form/post-form-type';
import {PostsFilterStateUrlQueryNameEnum} from '../../page/main/posts/posts-filter/posts-filter-type';
import {serialize} from '../../util/api-adapter';
import {getEnumValue} from '../../util/enum';
import {QueryMapType} from '../../util/type';

import {
    postAiSuggestImageActionSchema,
    PostAiSuggestImageActionType,
    PostAiSuggestItemType,
    postAiSuggestLegacyActionSchema,
    PostAiSuggestLegacyActionType,
    postAiSuggestTextActionSchema,
    PostAiSuggestTextActionType,
    postAiSuggestTextPayloadSchema,
    PostFormFieldsEnum,
    PostFormType,
    PostsFilterKeyEnum,
    PostsFilterStateType,
    PostStatusEnum,
    SourcePostFormFieldsEnum,
} from './posts-types';

export function getInitialPostsFilter(urlQueries: QueryMapType): PostsFilterStateType {
    const sourcesRaw = urlQueries[PostsFilterStateUrlQueryNameEnum.catalogs];
    const filterSources =
        typeof sourcesRaw === 'string' && sourcesRaw.length > 0
            ? sourcesRaw.split(',').map((sourceIterInRow) => {
                  return Number.parseInt(sourceIterInRow, 10);
              })
            : [];
    const statusesRaw = urlQueries[PostsFilterStateUrlQueryNameEnum.statuses];

    const filterStatuses =
        typeof statusesRaw === 'string' && statusesRaw.length > 0
            ? statusesRaw.split(',').reduce<Array<PostStatusEnum>>((accumulator, statusRaw) => {
                  const status = getEnumValue(PostStatusEnum, statusRaw);

                  if (status) {
                      return [...accumulator, status];
                  }

                  return accumulator;
              }, [])
            : [];

    const dateFromRaw = urlQueries[PostsFilterStateUrlQueryNameEnum.createdAtAfter];
    const dateFrom: Date | null = dateFromRaw ? new Date(dateFromRaw) : null;

    const dateToRaw = urlQueries[PostsFilterStateUrlQueryNameEnum.createdAtBefore];
    const dateTo: Date | null = dateToRaw ? new Date(dateToRaw) : null;

    return {
        [PostsFilterKeyEnum.sources]: filterSources,
        [PostsFilterKeyEnum.statuses]: filterStatuses,
        [PostsFilterKeyEnum.timeRange]: [dateFrom, dateTo],
    };
}

export function isAiSuggestTextAction(data: unknown): data is PostAiSuggestTextActionType {
    return postAiSuggestTextActionSchema.safeParse(data).success;
}

export function isAiSuggestImageAction(data: unknown): data is PostAiSuggestImageActionType {
    return postAiSuggestImageActionSchema.safeParse(data).success;
}

// Legacy suggestions event (ai_post_generation_v2 OFF). Checked after the v2 guards because it has no
// `type` field; a v2 text/image event never matches (it lacks `postSuggestions`).
export function isAiSuggestLegacyAction(data: unknown): data is PostAiSuggestLegacyActionType {
    return postAiSuggestLegacyActionSchema.safeParse(data).success;
}

// The Centrifugo text event carries a RAW JSON string in `action.text`: {"text": string, "cta": {...} | null}.
// On any parse/shape error we fall back to treating the raw string as the post text with no CTA.
export function parseTextActionSuggest(action: PostAiSuggestTextActionType): PostAiSuggestItemType {
    try {
        const parsed = postAiSuggestTextPayloadSchema.parse(JSON.parse(action.text));

        return {text: parsed.text, cta: parsed.cta ?? null};
    } catch {
        return {text: action.text, cta: null};
    }
}

export function getCentrifugePostSuggestChannel(options: {channelId: string; userId: string}): string {
    return `posts:suggests_${options.channelId}#${options.userId}`;
}

export const FORM_STEP_FIELDS: Record<PostStepEnum, Array<PostFormFieldsEnum>> = {
    [PostStepEnum.Target]: [
        PostFormFieldsEnum.Brands,
        PostFormFieldsEnum.Name,
        PostFormFieldsEnum.SelectorId,
        PostFormFieldsEnum.VkGroupsIds,
        PostFormFieldsEnum.FbPagesIds,
        PostFormFieldsEnum.InstagramGroupsIds,
    ],
    [PostStepEnum.CommonContent]: [],
    [PostStepEnum.AdaptiveContent]: [PostFormFieldsEnum.Posts],
    [PostStepEnum.SendParameters]: [
        PostFormFieldsEnum.Timezone,
        PostFormFieldsEnum.CreateDateTime,
        PostFormFieldsEnum.DeleteDateTime,
    ],
};

function prepareDateTimeForSending(date: Dayjs | null): string | undefined {
    // we work with device timezone in datePickers but send it like it was chosen in specified timezone

    if (!date) {
        // eslint-disable-next-line no-undefined
        return undefined;
    }

    return date.tz('UTC', true).toISOString();
}

export function preparePostFormRequestData(formData: PostFormType, status: PostStatusEnum): string {
    return JSON.stringify(
        serialize({
            ...formData,
            [PostFormFieldsEnum.Status]: status,
            [PostFormFieldsEnum.DeleteDateTime]: prepareDateTimeForSending(formData[PostFormFieldsEnum.DeleteDateTime]),
            [PostFormFieldsEnum.CreateDateTime]: prepareDateTimeForSending(formData[PostFormFieldsEnum.CreateDateTime]),
            time: null,
            date: null,
            isAdaptiveContent: true,
            posts: formData.posts.map((post) => ({
                title: null,
                ...post,
                ...(post[SourcePostFormFieldsEnum.Event]
                    ? {
                          [SourcePostFormFieldsEnum.Event]: {
                              title: post[SourcePostFormFieldsEnum.Event].title,
                              start: post[SourcePostFormFieldsEnum.Event].start.utc(true).toISOString(),
                              end: post[SourcePostFormFieldsEnum.Event].end.utc(true).toISOString(),
                          },
                      }
                    : {}),
            })),
        })
    );
}
